// Step 2 - the domain list.
//
// Order is the contract. Domain on line N here is the domain that receives line
// N of the verification box (step 6) and line N of the DKIM box (step 7), so a
// re-submit that shifts positions silently re-points every value typed later.
// The server refuses the change once a stage past probe has run; before that,
// the confirm below is the only thing standing between a re-check and an
// afternoon of pasted codes.

import { useEffect, useMemo, useRef, useState } from 'react';

import {
  fdApi,
  fdErrorCode,
  fdErrorMessage,
  type ApiError,
  type FdDomain,
  type FdNoValidDomainsData,
  type FdSetDomainsResult,
} from '../foreignDnsApi';
import {
  findStage,
  isZoneNotFound,
  summaryCount,
  useEscapeToClose,
  type FdStepProps,
} from './shared';

const LINE_SPLIT = /\r\n|\r|\n/;

const TEXTAREA_ID = 'fd-domains-text';

/**
 * FALLBACK "As typed" annotation, for rows written before `input_as_typed`
 * existed. The authoritative source is now `FdDomain.input_as_typed`, stored by
 * replaceRunDomains from `ParsedDomain.input`; this only fills the gap on older
 * rows and survives a page reload no better than it ever did.
 *
 * Display only, and deliberately NOT a second copy of the server's parser. The
 * pairing is a plain equality-or-substring test against the lines the operator
 * submitted in THIS browser session. A row is labelled only when the answer is
 * certain: `unchanged` when some submitted line IS the stored domain, the raw
 * line when exactly one submitted line contains it, and nothing at all when
 * neither holds. It cannot disagree with what was written, because the write
 * already happened and the authoritative `domain` column sits beside it.
 */
function annotateAsTyped(submitted: string, domains: FdDomain[]): Map<string, string> {
  const lines = submitted
    .split(LINE_SPLIT)
    .map((l) => l.trim())
    .filter((l) => l !== '');
  const out = new Map<string, string>();
  for (const d of domains) {
    const needle = d.domain.toLowerCase();
    if (lines.some((l) => l.toLowerCase() === needle)) {
      out.set(d.id, 'unchanged');
      continue;
    }
    const hits = lines.filter((l) => l.toLowerCase().includes(needle));
    if (hits.length === 1) out.set(d.id, hits[0]);
  }
  return out;
}

/**
 * What the As typed column shows for one row. The stored raw line wins whenever
 * there is one; the session heuristic only covers rows that predate the column.
 * A line that survived normalisation unchanged is labelled rather than printed
 * twice, so a rewrite is the only thing that draws the eye.
 */
function asTypedLabel(d: FdDomain, fallback: Map<string, string> | null): string {
  const stored = d.input_as_typed;
  if (stored !== null) {
    return stored.toLowerCase() === d.domain.toLowerCase() ? 'unchanged' : stored;
  }
  return fallback?.get(d.id) ?? '-';
}

/** ApiError.data on a NO_VALID_DOMAINS failure, or null if it is not there. */
function noValidDomainsData(err: unknown): FdNoValidDomainsData | null {
  if (fdErrorCode(err) !== 'NO_VALID_DOMAINS') return null;
  const data = (err as ApiError).data as Partial<FdNoValidDomainsData> | undefined;
  if (!data || !Array.isArray(data.duplicates) || !Array.isArray(data.invalid)) return null;
  return { duplicates: data.duplicates, invalid: data.invalid };
}

function zoneBadge(d: FdDomain): JSX.Element {
  // Keyed off the zone columns, never off last_error: probe writes an advisory
  // there on rows that are perfectly healthy.
  if (isZoneNotFound(d)) {
    return <span className="fd-badge fd-badge--err">zone not found</span>;
  }
  const active = d.zone_status === 'active';
  return (
    <span className={`fd-badge fd-badge--${active ? 'ok' : 'warn'}`}>
      {d.zone_status ?? 'unknown'}
    </span>
  );
}

function capChip(label: string, value: FdDomain['cap_redirect_rules']): JSX.Element {
  return <span className={`fd-chip fd-chip--${value}`}>{label}</span>;
}

export function StepDomains({
  runId,
  detail,
  reload,
  notifyEnqueued,
  goToStep,
  toast,
}: FdStepProps): JSX.Element {
  const { domains, stages } = detail;
  const probe = findStage(stages, 'probe');
  const probing = probe?.status === 'running';

  // The list the SERVER holds, as textarea content.
  const serverText = useMemo(() => domains.map((d) => d.domain).join('\n'), [domains]);

  const [text, setText] = useState(serverText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FdSetDomainsResult | null>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  /**
   * Re-seed the box when the server's list changes underneath an UNTOUCHED
   * field.
   *
   * Two operators on one run is explicitly supported, and this box seeded once
   * on mount and never again. So B replaces the list, A's screen keeps showing
   * the old list, and A's "Replace domain list" - which A reads as a harmless
   * re-check - writes the stale list back at NEW positions. Position is the
   * contract every later paste-spill maps onto, so that silently re-points
   * every code typed afterwards.
   *
   * "Untouched" is `text === the last server list we seeded from`, not a
   * separate dirty flag: it self-heals after A's own save (server catches up to
   * what A typed) and it never discards edits A has not saved yet.
   */
  const seededFrom = useRef(serverText);
  useEffect(() => {
    if (serverText === seededFrom.current) return;
    const untouched = text === seededFrom.current;
    seededFrom.current = serverText;
    if (untouched) setText(serverText);
  }, [serverText, text]);

  // Memoised: this is O(lines x domains) and the shell re-renders every 3s while
  // a stage is running.
  const asTyped = useMemo(
    () => (submitted === null ? null : annotateAsTyped(submitted, domains)),
    [submitted, domains],
  );

  useEscapeToClose(confirmReplace, busy, () => setConfirmReplace(false));

  async function save(): Promise<void> {
    const value = text;
    if (value.trim() === '') return;
    setBusy(true);
    setError(null);
    setConfirmReplace(false);
    try {
      const res = await fdApi.setDomains(runId, value);
      setResult(res);
      setSubmitted(value);
      if (res.alreadyRunning) {
        // replaceRunDomains DID run - the list is saved. Only the probe job was
        // refused, because an identical one is mid-flight and BullMQ would not
        // let it be displaced. Saying "probing" here would be a lie about a job
        // that is scanning the PREVIOUS list.
        toast(
          'warn',
          `${res.count} domain(s) saved, but the domain probe is already running so a new one was not started. Wait for it to finish, then use Re-check domains.`,
        );
      } else {
        notifyEnqueued();
        toast('success', `${res.count} domain(s) saved. Probing Cloudflare...`);
      }
      await reload();
    } catch (err) {
      const partial = noValidDomainsData(err);
      if (partial) setResult({ ...partial, count: 0, enqueued: false, alreadyRunning: false });
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function recheck(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await fdApi.runStage(runId, 'probe');
      if (res.alreadyRunning) {
        toast('warn', 'The domain probe is already running - nothing new was started.');
      } else {
        notifyEnqueued();
        toast('success', 'Re-checking every domain against Cloudflare...');
      }
      await reload();
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const probeOk = summaryCount(probe, 'ok');
  const probeFailed = summaryCount(probe, 'failed');

  return (
    <section className="fd-step">
      <div className="fd-step-head">
        <h2>Step 2 - Domains</h2>
        <p>
          Each surviving domain is resolved against the customer's Cloudflare account: its zone,
          that zone's forwarding capabilities, and its existing DNS records.
        </p>
      </div>
      <div className="fd-step-body">
        <div className="fd-field">
          <label className="fd-field-label" htmlFor={TEXTAREA_ID}>
            One domain per line. The order here is the order used for verification codes and DKIM
            values later, so keep it identical to your sheet.
          </label>
          <textarea
            id={TEXTAREA_ID}
            className="fd-textarea fd-mono"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'acme.com\nacme-mail.com\nmail.acme.co.uk'}
            spellCheck={false}
            disabled={busy}
          />
          <span className="fd-hint">
            Each line is trimmed, lower-cased and stripped of any scheme, path or trailing dot. A
            leading <span className="fd-mono">www.</span> is removed, because it is never the mail
            host. Repeats are dropped keeping the first one.
          </span>
        </div>

        {error && <div className="fd-notice fd-notice--err">{error}</div>}

        {result && (result.duplicates.length > 0 || result.invalid.length > 0) && (
          <div className="fd-notice fd-notice--warn">
            {/* The server reports duplicates as the NORMALISED host and invalid
                lines verbatim, because a repeat is only detectable after
                normalisation. Labelled so neither list is read as the other. */}
            {result.duplicates.length > 0 && (
              <div>
                <strong>
                  Dropped as repeats ({result.duplicates.length}), shown normalised:
                </strong>{' '}
                <span className="fd-mono">{result.duplicates.join(', ')}</span>
              </div>
            )}
            {result.invalid.length > 0 && (
              <div>
                <strong>
                  Did not parse as a domain ({result.invalid.length}), shown as typed:
                </strong>{' '}
                <span className="fd-mono">{result.invalid.join(', ')}</span>
              </div>
            )}
            <div>Reconcile these against the sheet before you continue.</div>
          </div>
        )}

        {probing && (
          <div className="fd-notice fd-notice--info">
            Probing Cloudflare for {domains.length} domain(s) - resolving each zone, reading its
            capabilities and pulling its existing DNS records. This page updates itself.
          </div>
        )}

        {!probing && probe?.status === 'done' && (
          <div className="fd-notice">
            Probe finished
            {probeOk === null ? '' : `: ${probeOk} resolved`}
            {probeFailed === null || probeFailed === 0 ? '' : `, ${probeFailed} with no zone`}.
          </div>
        )}

        {!probing && probe?.status === 'failed' && (
          <div className="fd-notice fd-notice--err">
            The probe stage failed: {probe.error_message ?? 'no message recorded'}
          </div>
        )}

        <div className="fd-actions">
          <button
            type="button"
            className="fd-btn fd-btn--primary"
            onClick={() => {
              if (domains.length > 0) setConfirmReplace(true);
              else void save();
            }}
            disabled={busy || text.trim() === ''}
          >
            {busy ? 'Saving...' : domains.length > 0 ? 'Replace domain list' : 'Save domains'}
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--secondary"
            onClick={() => void recheck()}
            disabled={busy || domains.length === 0 || probing}
          >
            Re-check domains
          </button>
          <div className="fd-actions-spacer" />
          <button
            type="button"
            className="fd-btn fd-btn--secondary"
            onClick={() => void goToStep(3)}
            disabled={busy || domains.every(isZoneNotFound)}
          >
            Continue to purge
          </button>
        </div>
      </div>

      {domains.length > 0 && (
        <div className="fd-table-wrap">
          <table className="fd-table">
            <thead>
              <tr>
                <th className="fd-col-num">#</th>
                <th>As typed</th>
                <th>Domain</th>
                <th className="fd-col-center">Zone</th>
                <th className="fd-col-center">Records</th>
                <th className="fd-col-center">Capabilities</th>
                <th className="fd-col-center" />
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => {
                const records = d.live_records;
                const open = expanded === d.id;
                const typed = asTypedLabel(d, asTyped);
                return [
                  <tr key={d.id}>
                    <td className="fd-col-num fd-cell-faint">{d.position + 1}</td>
                    <td className="fd-cell-mono fd-cell-faint">{typed}</td>
                    <td className="fd-cell-mono">{d.domain}</td>
                    <td className="fd-col-center">{zoneBadge(d)}</td>
                    <td className="fd-col-center fd-cell-faint">
                      {records === null ? '-' : records.length}
                    </td>
                    <td className="fd-col-center">
                      {capChip('Redirect Rules', d.cap_redirect_rules)}{' '}
                      {capChip('Page Rules', d.cap_page_rules)}
                    </td>
                    <td className="fd-col-center">
                      <button
                        type="button"
                        className="fd-btn fd-btn--ghost fd-btn--small"
                        onClick={() => setExpanded(open ? null : d.id)}
                        disabled={records === null || records.length === 0}
                      >
                        {open ? 'Hide records' : 'Show records'}
                      </button>
                    </td>
                  </tr>,
                  open && records !== null ? (
                    <tr key={`${d.id}-records`}>
                      <td colSpan={7}>
                        <table className="fd-table fd-mono">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Name</th>
                              <th>Content</th>
                              <th className="fd-col-center">TTL</th>
                              <th className="fd-col-center">Prio</th>
                              <th className="fd-col-center">Proxied</th>
                            </tr>
                          </thead>
                          <tbody>
                            {records.map((r) => (
                              <tr key={r.id}>
                                <td className="fd-cell-mono">{r.type}</td>
                                <td className="fd-cell-mono">{r.name}</td>
                                <td className="fd-cell-mono">{r.content}</td>
                                <td className="fd-col-center fd-cell-faint">{r.ttl}</td>
                                <td className="fd-col-center fd-cell-faint">
                                  {r.priority ?? '-'}
                                </td>
                                <td className="fd-col-center fd-cell-faint">
                                  {r.proxied === undefined ? '-' : r.proxied ? 'yes' : 'no'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      {domains.length > 0 && (
        <div className="fd-step-body">
          <span className="fd-hint">
            The <strong>As typed</strong> column is the raw line you pasted, kept by the server so
            a silent rewrite stays visible - <span className="fd-mono">www.acme.com</span> is stored
            as <span className="fd-mono">acme.com</span>, and a scheme, a path and a trailing dot are
            all dropped. Rows created before this run of the tool have no stored line and fall back
            to matching against this session's paste, so they can read{' '}
            <span className="fd-mono">-</span> after a reload. Existing records come from the probe;
            step 8 re-reads them live from Cloudflare.
          </span>
        </div>
      )}

      {confirmReplace && (
        <div className="fd-modal-overlay">
          <div className="fd-modal">
            <div className="fd-modal-header">
              <h3 className="fd-modal-title">Replace the domain list?</h3>
            </div>
            <div className="fd-modal-body">
              <p>
                This deletes and re-creates all {domains.length} row(s) on this run. Every
                verification code and DKIM value already typed is cleared, along with the resolved
                zone ids, because position is the contract that maps those values to domains.
              </p>
              <p>
                To re-read the zones without touching the list, close this and use{' '}
                <strong>Re-check domains</strong> instead.
              </p>
            </div>
            <div className="fd-modal-footer">
              <button
                type="button"
                className="fd-btn fd-btn--danger"
                onClick={() => void save()}
                disabled={busy}
              >
                Replace the list
              </button>
              <button
                type="button"
                className="fd-btn fd-btn--secondary"
                onClick={() => setConfirmReplace(false)}
                disabled={busy}
              >
                Keep what is there
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
